import { type ReactNode, useRef } from 'react';
import type { Row } from '@tanstack/react-table';

import { useDeviceTypeDetection } from '@/ux-common/hooks/use-device-type-detection/use-device-type-detection';

import {
  Cache,
  Cell,
  Column,
  Density,
  DragScroll,
  Filter,
  GlobalFilter,
  Header,
  ScrollTo,
  Settings,
  Sorting,
  StickyHeader,
  Styles,
  Virtualize,
} from './config';
import { TableProvider } from './context';
import { TableRenderer } from './rendering';
import { TableTooltipIsland } from './table-tooltip-island';
import type { DataTableProps, ParsedChildren, TableContextValue } from './types';
import { useTableState } from './use-table-state';
import { parseChildren, serializeColumns, serializeSettings } from './utils';
import { CellHighlight } from '../features/cell-highlight';
import { ColumnOverlay } from '../features/column-overlay';
import { BulkSelectBar, SelectColumn } from '../features/selection';

// =============================================================================
// Main DataTable Component
// =============================================================================

/**
 * DataTable2 - A simplified, high-performance data table built on TanStack Table.
 *
 * Uses React Context for state isolation (no jotai-scope), declarative column
 * configuration via JSX children, and efficient render-phase parsing.
 *
 * Supports both controlled and uncontrolled state management:
 * - **Uncontrolled**: Use `initial` prop on Filter/Sorting components
 * - **Controlled**: Use `value` prop on Filter/Sorting components (parent owns state)
 *
 * @example Basic usage
 * ```tsx
 * <DataTable id="users" data={users}>
 *   <DataTable.Column id="name" accessor="name">
 *     <DataTable.Header>Name</DataTable.Header>
 *   </DataTable.Column>
 *   <DataTable.Column id="email" accessor="email">
 *     <DataTable.Header>Email</DataTable.Header>
 *   </DataTable.Column>
 * </DataTable>
 * ```
 *
 * @example Uncontrolled with initial state
 * ```tsx
 * <DataTable id="users" data={users}>
 *   <DataTable.Filter initial={[{ id: 'status', value: 'active' }]} />
 *   <DataTable.Sorting initial={[{ id: 'name', desc: false }]} />
 *   <DataTable.Column id="name" accessor="name" sortable>
 *     <DataTable.Header>Name</DataTable.Header>
 *   </DataTable.Column>
 * </DataTable>
 * ```
 *
 * @example Controlled mode (parent owns state)
 * ```tsx
 * const [filters, setFilters] = useState<ColumnFiltersState>([]);
 * const [sorting, setSorting] = useState<SortingState>([]);
 *
 * <DataTable id="users" data={users}>
 *   <DataTable.Filter value={filters} onChange={setFilters} />
 *   <DataTable.Sorting value={sorting} onChange={setSorting} />
 *   <DataTable.Column id="name" accessor="name" sortable>
 *     <DataTable.Header>Name</DataTable.Header>
 *   </DataTable.Column>
 * </DataTable>
 * ```
 */
const DataTableRoot = <TData,>({
  id,
  data,
  width = 'auto',
  defaultColumnSize,
  onRowClick,
  isLoading,
  className,
  getRowId,
  emptyMessage,
  children,
}: DataTableProps<TData>): ReactNode => {
  const { isMobile } = useDeviceTypeDetection();

  // ---------------------------------------------------------------------------
  // Parse Children
  // ---------------------------------------------------------------------------

  // Parse children during render (this is cheap - just traversing React elements)
  const parsedChildren = parseChildren<TData>(children);

  // Track previous serialized state for structural equality comparison
  // This prevents unnecessary re-creates of the table when parent re-renders
  // but column configuration and settings haven't actually changed
  const prevSerializedColumnsRef = useRef<string>('');
  const prevSerializedSettingsRef = useRef<string>('');
  const stableParsedRef = useRef<ParsedChildren<TData>>(parsedChildren);

  const serializedColumns = serializeColumns(parsedChildren.columns);
  const serializedSettings = serializeSettings(parsedChildren.settings);
  if (
    serializedColumns !== prevSerializedColumnsRef.current ||
    serializedSettings !== prevSerializedSettingsRef.current
  ) {
    prevSerializedColumnsRef.current = serializedColumns;
    prevSerializedSettingsRef.current = serializedSettings;
    stableParsedRef.current = parsedChildren;
  }

  // Combine stable columns with fresh filter/sorting/selection config for controlled mode
  // Note: We intentionally don't useMemo here - columns are stable via ref,
  // and filter/sorting/selection need to update on every render for controlled mode
  const stableParsedChildren: ParsedChildren<TData> = {
    ...stableParsedRef.current,
    // Always use fresh filter/sorting/selection config for controlled mode support
    filter: parsedChildren.filter,
    globalFilter: parsedChildren.globalFilter,
    sorting: parsedChildren.sorting,
    selection: parsedChildren.selection,
    scrollTo: parsedChildren.scrollTo,
  };

  // ---------------------------------------------------------------------------
  // Create Table State
  // ---------------------------------------------------------------------------

  const { contextValue } = useTableState({
    tableId: id,
    data,
    parsedChildren: stableParsedChildren,
    isMobile,
    widthMode: width,
    defaultColumnSize,
    getRowId,
    emptyMessage,
  });

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <TableProvider value={contextValue as TableContextValue}>
      <TableTooltipIsland>
        <div
          className={`data-table2 relative ${width === 'full' ? 'w-full' : 'w-auto'} ${className ?? ''}`}
          data-table-id={id}
          data-loading={isLoading}
        >
          <TableRenderer
            onRowClick={onRowClick as ((row: Row<unknown>) => void) | undefined}
            bulkSelectBar={parsedChildren.bulkSelectBar}
            columnOverlay={parsedChildren.columnOverlay}
            cellHighlight={parsedChildren.cellHighlight}
          />
        </div>
      </TableTooltipIsland>
    </TableProvider>
  );
};

// =============================================================================
// Compound Component Assembly
// =============================================================================

/**
 * DataTable compound component with all sub-components attached.
 */
export const DataTable = Object.assign(DataTableRoot, {
  // Config components
  Column,
  Header,
  Cell,
  // Settings components
  Settings,
  Density,
  StickyHeader,
  Cache,
  Virtualize,
  DragScroll,
  Styles,
  // State management components
  Filter,
  GlobalFilter,
  Sorting,
  // Scroll
  ScrollTo,
  // Feature components
  SelectColumn,
  BulkSelectBar,
  ColumnOverlay,
  CellHighlight,
});
